import { ResourceSlot } from '__types__';
import { isEmpty, omit, pickBy } from 'lodash';
import { UseFormReturn } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { updateBooking } from 'store/v1/bookings/bookings.actions';
import { updateResourceSlot } from 'store/v1/productions/productions.actions';
import { isFieldOrParentDirty } from 'v5/platform/react/useDirtyFields';
import { BookingEntity, BookingEntitySpec } from '../types';

export function useUpdateBookingEntity(spec: BookingEntitySpec) {
  const dispatch = useDispatch();

  return async (form: UseFormReturn<BookingEntity>) => {
    const payload = form.getValues();
    if (form.formState.dirtyFields.estimateValue) {
      const changedRateFields: Partial<BookingEntity['estimateValue']['rate']> =
        pickBy(payload.estimateValue.rate, (value, field) =>
          isFieldOrParentDirty(
            form.formState.dirtyFields,
            `estimateValue.rate.${field}`
          )
        );
      const changedFeesFields: Partial<BookingEntity['estimateValue']['fees']> =
        pickBy(payload.estimateValue.fees, (value, field) =>
          isFieldOrParentDirty(
            form.formState.dirtyFields,
            `estimateValue.fees.${field}`
          )
        );

      const resourceSlotPayload: Partial<ResourceSlot> = pickBy(
        {
          estimate_rate_agency_fee: changedFeesFields.agency,
          estimate_rate_payroll_fee: changedFeesFields.payroll,
          estimate_rate_processing_fee: changedFeesFields.processing,
          estimate_rate_amount: changedRateFields.amount,
          estimate_rate_currency: changedRateFields.currency,
          estimate_rate_interval: changedRateFields.interval,
          estimate_rate_quantity: changedRateFields.quantity
        },
        value => value !== undefined
      );

      if (!isEmpty(resourceSlotPayload)) {
        await dispatch(
          updateResourceSlot(
            {
              production_id: spec.productionId,
              resource_slot_id: spec.slotId
            },
            resourceSlotPayload
          )
        );
      }
    }
    if (form.formState.dirtyFields.actualValue && payload.actualValue) {
      const changedRateFields = pickBy(
        payload.actualValue.rate,
        (value, field) =>
          isFieldOrParentDirty(
            form.formState.dirtyFields,
            `actualValue.rate.${field}`
          )
      );
      const changedFeesFields = pickBy(
        payload.actualValue.fees,
        (value, field) =>
          isFieldOrParentDirty(
            form.formState.dirtyFields,
            `actualValue.fees.${field}`
          )
      );

      const changedExpensesFields = payload.actualValue.expenses.map(
        (expense, index) =>
          isFieldOrParentDirty(
            form.formState.dirtyFields,
            `actualValue.expenses.${index}`
          )
            ? omit(
                expense,
                'slot_category',
                'receipts',
                'user_id'
              )
            : { id: expense.id }
      );

      const bookingPayload = pickBy(
        {
          rate_agency_fee: changedFeesFields.agency,
          rate_payroll_fee: changedFeesFields.payroll,
          rate_processing_fee: changedFeesFields.processing,
          rate_amount: changedRateFields.amount,
          rate_currency: changedRateFields.currency,
          rate_interval: changedRateFields.interval,
          expenses: isFieldOrParentDirty(
            form.formState.dirtyFields,
            'actualValue.expenses'
          )
            ? changedExpensesFields
            : undefined
        },
        value => value !== undefined
      );
      if (!isEmpty(bookingPayload)) {
        const t = await dispatch(updateBooking(spec.bookingId, bookingPayload));
        if (t.error) {
          throw t.error;
        }
      }
    }
  };
}
